import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { StatCard } from "@/components/common/StatCard";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Building2, Hospital, Users, Layers, Activity, CheckCircle2, Loader2 } from "lucide-react";
import { MODULE_LABELS } from "@/lib/modules";

export default function PlatformDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/platform/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;

  const usage = Object.entries(data.module_usage || {}).sort((a, b) => b[1] - a[1]);
  const maxUse = Math.max(1, ...usage.map((u) => u[1]));

  return (
    <div data-testid="platform-dashboard">
      <PageHeader title="Platform Overview" subtitle="Live metrics across all healthcare organisations on Mediva Solutions." />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard label="Organisations" value={data.total_organisations} icon={Building2} hint={`${data.active_organisations} active`} testId="stat-organisations" />
        <StatCard label="Facilities" value={data.total_facilities} icon={Hospital} tone="emerald" hint={`${data.active_facilities} active`} testId="stat-facilities" />
        <StatCard label="Users" value={data.total_users} icon={Users} tone="slate" hint="excluding platform admins" testId="stat-users" />
        <StatCard label="Active Modules" value={usage.length} icon={Layers} tone="amber" hint="in use across tenants" testId="stat-modules" />
        <StatCard label="System" value="Online" icon={Activity} tone="emerald" hint="all services healthy" testId="stat-system" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md">
          <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-display font-semibold text-slate-800">Organisations</h3>
            <span className="text-xs text-slate-400">{data.organisations.length} total</span>
          </div>
          <div className="divide-y divide-slate-100">
            {data.organisations.map((o) => (
              <div key={o.id} className="px-5 py-3 flex items-center justify-between hover:bg-slate-50" data-testid={`dash-org-${o.id}`}>
                <div className="flex items-center gap-3">
                  <span className="h-9 w-9 rounded-md bg-slate-100 text-slate-500 grid place-items-center"><Building2 className="h-4 w-4" /></span>
                  <div>
                    <div className="text-sm font-medium text-slate-800 flex items-center gap-2">{o.name}{o.is_demo && <span className="text-[10px] font-mono uppercase text-amber-600 bg-amber-50 border border-amber-200 rounded px-1">demo</span>}</div>
                    <div className="text-xs text-slate-400">{o.facilities_count} facilities · {o.subscription_plan}</div>
                  </div>
                </div>
                <StatusBadge status={o.status} />
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md">
          <div className="px-5 py-3.5 border-b border-slate-200"><h3 className="font-display font-semibold text-slate-800">Module Usage</h3></div>
          <div className="p-5 space-y-3">
            {usage.length === 0 && <div className="text-sm text-slate-400">No modules activated yet.</div>}
            {usage.map(([code, count]) => (
              <div key={code}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-slate-600 font-medium">{MODULE_LABELS[code] || code}</span>
                  <span className="text-slate-400 font-mono">{count}</span>
                </div>
                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-blue-600 rounded-full" style={{ width: `${(count / maxUse) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md mt-6">
        <div className="px-5 py-3.5 border-b border-slate-200"><h3 className="font-display font-semibold text-slate-800">Recent Platform Activity</h3></div>
        <div className="divide-y divide-slate-100">
          {(data.recent_activity || []).map((a, i) => (
            <div key={i} className="px-5 py-2.5 flex items-center gap-3 text-sm">
              <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
              <span className="text-slate-700">{a.description}</span>
              <span className="ml-auto text-xs text-slate-400 font-mono">{new Date(a.timestamp).toLocaleString()}</span>
            </div>
          ))}
          {(!data.recent_activity || data.recent_activity.length === 0) && <div className="px-5 py-4 text-sm text-slate-400">No activity yet.</div>}
        </div>
      </div>
    </div>
  );
}
